import * as React from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import type { SortDirection } from '@klareospace/pharos-lib-types';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';

export interface SortableColumnHeaderProps<T extends string = string> {
  className?: string;
  columnKey: T;
  currentSortBy?: T;
  currentSortDirection?: SortDirection;
  label: string;
  onSort: (sortBy: T, sortDirection: SortDirection | null) => void;
}

function getSortIcon(isSorted: boolean, direction?: SortDirection): React.JSX.Element {
  if (!isSorted) {
    return <ArrowUpDown className="h-3 w-3 opacity-50" />;
  }
  return direction === 'asc' ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

function getNextSortDirection(isSorted: boolean, currentDirection?: SortDirection): SortDirection | null {
  if (!isSorted) return 'asc';
  if (currentDirection === 'asc') return 'desc';
  return null;
}

export function SortableColumnHeader<T extends string = string>({
  className,
  columnKey,
  currentSortBy,
  currentSortDirection,
  label,
  onSort,
}: SortableColumnHeaderProps<T>): React.JSX.Element {
  const isSorted = currentSortBy === columnKey;

  function handleClick(): void {
    const nextDirection = getNextSortDirection(isSorted, currentSortDirection);
    onSort(columnKey, nextDirection);
  }

  return (
    <Button
      className={cn('-ms-4 h-8 font-medium hover:text-foreground-hover', isSorted && 'text-foreground', className)}
      onClick={handleClick}
      variant="ghost"
    >
      {label}
      {getSortIcon(isSorted, currentSortDirection)}
    </Button>
  );
}
